import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {catchError, finalize, Observable, of, shareReplay, switchMap, throwError} from 'rxjs';
import {environment} from '@/environments/environment';
import {portalReasonForAuthError, redirectToPortal} from '@services/portal-redirect';
import {AuthService} from '@services/auth.service';
import {EventData, EventService} from '@services/event.service';
import {ActivatedRoute} from "@angular/router";

@Injectable()
export class BasicAuthInterceptor implements HttpInterceptor {

    private secureRoutes = [`${environment.schema}://${environment.apiUrl}`];

    private inFlightRefresh$: Observable<string> | null = null;

    constructor(private authService: AuthService,
                private eventService: EventService,
                private route: ActivatedRoute,
    )
    {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const isExternal = this.route.snapshot.children?.[0]?.url[0]?.path.toLowerCase() === 'external';
        if (!this.secureRoutes.find((x) => request.url.startsWith(x)) || isExternal) {
            return next.handle(request);
        }

        return this.authService.getAccessToken().pipe(
            switchMap((token) => this.requestWithToken(request, next, token)),
            catchError((error) => {
                if (error instanceof HttpErrorResponse && error.status === 403) {
                    const reason = portalReasonForAuthError(error.error);
                    if (reason) redirectToPortal(reason);
                }

                if (error instanceof HttpErrorResponse && error.status === 401) {
                    return this.refreshAccessToken().pipe(
                        switchMap((token) => this.requestWithToken(request, next, token))
                    );
                }

                throw error;
            })
        );
    }

    /**
     * Обновление токена одно на все параллельные 401: первый уходит в сеть, остальные
     * подцепляются к тому же результату через shareReplay. Отдельный Subject с take(1)
     * для этого не годился — подписчик, пришедший после сигнала, ждал бы вечно.
     * finalize обнуляет поле на завершении потока, поэтому следующий 401 снова пробует
     * обновить токен сам.
     */
    private refreshAccessToken(): Observable<string> {
        if (!this.inFlightRefresh$) {
            this.inFlightRefresh$ = this.authService.forceRefreshSession().pipe(
                // Признак «сессия жива» берём у библиотеки: silentRenew сам обновляет токен,
                // а если refresh невозможен (нет refresh token, invalid_grant), значит сессия
                // закончилась — уходим на Keycloak с возвратом в тот же раздел.
                switchMap(() => this.authService.getAccessToken()),
                switchMap((token) => token
                    ? of(token)
                    // Токена после обновления нет: повтор не поможет, запрос ушёл бы с пустым
                    // Authorization и снова получил бы 401, уйдя в рекурсию.
                    : throwError(() => new HttpErrorResponse({status: 401, statusText: 'No access token after refresh'}))),
                catchError((error) => {
                    this.handleRefreshFailure(error);
                    return throwError(() => error);
                }),
                finalize(() => this.inFlightRefresh$ = null),
                shareReplay({bufferSize: 1, refCount: false}),
            );
        }

        return this.inFlightRefresh$;
    }

    /**
     * Обновление не вышло. 403 с известной причиной — уводим в портал. 400/401 — Keycloak
     * сессию больше не отдаёт, выходим на вход. Всё прочее (обрыв сети, 403 без причины)
     * на вход не отправляет: сессия может быть жива, виноват канал связи или права.
     */
    private handleRefreshFailure(error: unknown): void {
        const response = error instanceof HttpErrorResponse ? error : null;
        const reason = response?.status === 403 ? portalReasonForAuthError(response.error) : null;
        if (reason) {
            redirectToPortal(reason);
            return;
        }

        if (response?.status === 400 || response?.status === 401) {
            this.eventService.emit(new EventData('endSession', response.message ?? null));
        }
    }

    private requestWithToken(request: HttpRequest<any>, next: HttpHandler, token: string): Observable<HttpEvent<any>> {
        return next.handle(request.clone({
            withCredentials: true,
            setHeaders: {Authorization: `Bearer ${token}`}
        }));
    }


}

